import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";
import { InfoIcon } from "../../../../../icons";
import { whatsappNumberForForm } from "../../../../../shared/domain/whatsappNumber";
import { createPatient } from "../../../application/patients";
import { validatePatient } from "../../../domain/patientValidators";
import { blankPersonDetails, type PersonDetails } from "../../../domain/PersonDetails";
import {
  CUSTOM_FIELD_ERRORS,
  CustomFieldInput,
  customFieldRefusal,
  useCenter,
  useCenterPatientFields,
  validateCustomValue,
} from "../../../../center";
import StudentCourseFields from "../StudentCourseFields";
import PersonFields from "../PersonFields";
import { PatientOwnershipChoice } from "../PatientOwnership";

interface PatientFormProps {
  therapistID: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: "easeOut" as const }
  },
};

export default function PatientAddForm({ therapistID, onClose, onSuccess }: PatientFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [person, setPerson] = useState<PersonDetails>(blankPersonDetails);

  const { center } = useCenter();
  const [heldByCenter, setHeldByCenter] = useState(true);
  const enrolsInCourse = center?.type === "training" && heldByCenter;

  const [courseId, setCourseId] = useState("");
  const [academicYear, setAcademicYear] = useState("");

  // The centre's own fields apply only to a patient the centre will hold.
  const heldByTheCenter = Boolean(center) && heldByCenter;
  const centerFields = useCenterPatientFields(center?.id);
  const showsCenterFields =
    heldByTheCenter && (centerFields.loading || centerFields.failed || centerFields.fields.length > 0);
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [customErrors, setCustomErrors] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (changes: Partial<PersonDetails>) => {
    setPerson((prev) => ({ ...prev, ...changes }));
    // A field's error goes as soon as it is edited; the save checks again.
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const field of Object.keys(changes)) delete next[field];
      return next;
    });
  };

  const handleCustomChange = (fieldId: string, value: string) => {
    setCustomValues((prev) => ({ ...prev, [fieldId]: value }));
    setCustomErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  // Sent whole whenever the fields are shown, so the centre's required ones are enforced.
  const sendsCustomValues = heldByTheCenter && centerFields.fields.length > 0;

  const handleSubmit = async () => {
    const errors = validatePatient(person);
    const customProblems: Record<string, string> = {};
    if (sendsCustomValues) {
      for (const field of centerFields.fields) {
        const problem = validateCustomValue(field, customValues[field.id] ?? "");
        if (problem) customProblems[field.id] = problem;
      }
    }
    if (Object.keys(errors).length > 0 || Object.keys(customProblems).length > 0) {
      setFieldErrors(errors);
      setCustomErrors(customProblems);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...person,
        therapist_id: therapistID,
        date_of_birth: person.date_of_birth || undefined,
        pronouns: person.pronouns || undefined,
        sex: person.sex || undefined,
        street: person.street || undefined,
        city: person.city || undefined,
        state: person.state || undefined,
        postal_code: person.postal_code || undefined,
        timezone: person.timezone || null,
        whatsapp_phone: whatsappNumberForForm(person.whatsapp_phone, person.country) ?? null,
        ...(center ? { held_by_center: heldByCenter } : {}),
        ...(enrolsInCourse
          ? {
              course_id: courseId || null,
              academic_year: courseId && academicYear ? Number(academicYear) : null,
            }
          : {}),
        ...(sendsCustomValues
          ? {
              custom_values: Object.fromEntries(
                centerFields.fields.map((field) => [field.id, (customValues[field.id] ?? "").trim() || null])
              ),
            }
          : {}),
      };
      const newPatient = await createPatient(payload);
      navigate(`/patient/${newPatient.id}`);
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      console.error(err);
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const refusal = customFieldRefusal(err);
      if (refusal.fieldId && centerFields.fields.some((field) => field.id === refusal.fieldId)) {
        setCustomErrors({ [refusal.fieldId]: refusal.message });
        setError(t("center.patientFields.createForm.error"));
      } else if (typeof detail === "string" && CUSTOM_FIELD_ERRORS[detail]) {
        setError(t(refusal.message));
      } else {
        setError(t("patient.saveError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ComponentCard
      glass={true}
      title={t("patient.information")}
      className="overflow-hidden shadow-xl border-white/20 dark:border-white/10"
    >
      <motion.form
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        {center && (
          <motion.div variants={itemVariants}>
            <PatientOwnershipChoice
              centerName={center.name}
              heldByCenter={heldByCenter}
              onChange={setHeldByCenter}
            />
          </motion.div>
        )}

        <motion.div variants={itemVariants}>
          <PersonFields
            idPrefix="patient"
            value={person}
            onChange={handleChange}
            errors={fieldErrors}
            showTimezoneHint
            autoFocus
          />
        </motion.div>

        {enrolsInCourse && center && (
          <motion.div variants={itemVariants}>
            <StudentCourseFields
              centerId={center.id}
              courseId={courseId}
              year={academicYear}
              onCourseChange={setCourseId}
              onYearChange={setAcademicYear}
              disabled={loading}
            />
          </motion.div>
        )}

        {showsCenterFields && center && (
          <motion.fieldset variants={itemVariants} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <legend className="mb-1 w-full border-b border-border-subtle pb-2 text-sm font-semibold text-content-primary">
              {t("center.patientFields.card.title")}
            </legend>
            <p className="text-xs text-content-secondary sm:col-span-2">
              {t("center.patientFields.createForm.hint", { center: center.name })}
            </p>
            {centerFields.failed ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-3 text-sm sm:col-span-2">
                <span className="text-status-critical">{t("center.patientFields.card.errorLoading")}</span>
                <Button size="sm" variant="outline" onClick={centerFields.retry}>
                  {t("center.patientFields.card.retry")}
                </Button>
              </div>
            ) : centerFields.loading ? (
              <p role="status" className="text-sm text-content-secondary sm:col-span-2">
                {t("common.loading")}
              </p>
            ) : (
              centerFields.fields.map((field) => (
                <CustomFieldInput
                  key={field.id}
                  field={field}
                  id={`patient-custom-${field.id}`}
                  value={customValues[field.id] ?? ""}
                  onChange={(value) => handleCustomChange(field.id, value)}
                  error={customErrors[field.id]}
                />
              ))
            )}
          </motion.fieldset>
        )}

        <motion.div
          variants={itemVariants}
          className="bg-brand-50/50 dark:bg-brand-900/10 border border-brand-100 dark:border-brand-800/50 rounded-xl p-4 flex gap-3"
        >
          <div className="mt-0.5">
            <InfoIcon className="size-5 text-brand-600 dark:text-brand-400" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-brand-900 dark:text-brand-100 mb-1">
              {t("patient.gdprNoticeTitle")}
            </h4>
            <p className="text-xs text-brand-700/80 dark:text-brand-400/80 leading-relaxed">
              {t("patient.gdprNoticeDesc")}
            </p>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="flex items-center justify-end pt-4 gap-3">
          {onClose && (
            <Button
              variant="outline"
              className="px-8"
              size="md"
              onClick={onClose}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="px-8 min-w-[120px]"
            size="md"
          >
            {t("common.save")}
          </Button>
        </motion.div>

        {error && (
          <motion.p
            role="alert"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 dark:text-red-400 text-sm text-center font-medium"
          >
            {error}
          </motion.p>
        )}
      </motion.form>
    </ComponentCard>
  );
}
